import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Circle, PhoneForwarded, BrainCircuit, CalendarCheck } from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'Connect your calls',
    description: 'Keep your current business number and forward calls to Miadesk. Or get a new AI number instantly — setup takes under 60 seconds.',
    icon: PhoneForwarded,
    color: 'purple'
  },
  {
    number: '02',
    title: 'Train your AI agent',
    description: 'Add your FAQs, services, business hours and custom greetings so your agent answers questions like a real team member.',
    icon: BrainCircuit,
    color: 'teal'
  },
  {
    number: '03',
    title: 'Capture leads & bookings',
    description: 'Your AI agent books appointments, qualifies leads via calls and chat, and sends you details instantly via text and email.',
    icon: CalendarCheck,
    color: 'blue'
  }
];

const colorConfig = {
  purple: {
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/20',
    iconBg: 'bg-purple-500/20',
    iconColor: 'text-purple-400',
    numColor: 'text-purple-500',
    line: 'from-purple-500/40'
  },
  teal: {
    bg: 'bg-teal-500/10',
    border: 'border-teal-500/20',
    iconBg: 'bg-teal-500/20',
    iconColor: 'text-teal-400',
    numColor: 'text-teal-500',
    line: 'from-teal-500/40'
  },
  blue: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    iconBg: 'bg-blue-500/20',
    iconColor: 'text-blue-400',
    numColor: 'text-blue-500',
    line: 'from-blue-500/40'
  }
};

const HowItWorks = () => {
  const navigate = useNavigate();

  return (
    <section id="how-it-works" className="py-20 px-6" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How it works
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Get set up fast — then your AI agent picks up every call and chat.
          </p>
        </div>

        {/* Steps */}
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {steps.map((step, i) => {
              const cfg = colorConfig[step.color];
              return (
                <div key={step.number} className="relative" data-testid={`step-${step.number}`}>
                  {/* Connector line */}
                  {i < steps.length - 1 && (
                    <div className={`absolute left-8 top-20 w-px h-6 bg-gradient-to-b ${cfg.line} to-transparent hidden md:block`} />
                  )}

                  <div className={`flex items-start gap-6 p-6 rounded-2xl ${cfg.bg} border ${cfg.border} backdrop-blur-sm transition-all hover:scale-[1.01]`}>
                    {/* Step number + icon */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-2">
                      <span className={`text-xs font-bold tracking-widest uppercase ${cfg.numColor}`}>
                        Step {step.number}
                      </span>
                      <div className={`w-14 h-14 rounded-xl ${cfg.iconBg} flex items-center justify-center`}>
                        <step.icon className={`w-7 h-7 ${cfg.iconColor}`} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="pt-1">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {step.title}
                      </h3>
                      <p className="text-gray-300 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center mt-14 gap-3">
          <Button
            onClick={() => navigate('/register')}
            className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white rounded-full px-8 py-6 text-base font-medium transition-all shadow-lg shadow-purple-500/25"
            data-testid="how-it-works-cta"
          >
            <Circle className="w-5 h-5 mr-2 fill-white" />
            Start Free Trial
          </Button>
          <span className="text-sm text-gray-400">
            Try it free for 7 days. No credit card required
          </span>
          <a href="#pricing" className="text-purple-400 hover:text-purple-300 text-sm underline underline-offset-4 mt-1">
            Compare plans
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
